
(() => {
  if (window.brentosBundleProgressInitialized) return;
  window.brentosBundleProgressInitialized = true;

  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;

  let refreshTimer;
  let lastRequest = 0;
  let positionFrame = null;
  let lastCart = null;

  // Dismissal lasts until the cart changes or the page reloads.
  const dismissed = new Set();

  function getSections() {
    return [...document.querySelectorAll('[data-brentos-bundle]')];
  }

  function getHandles(section) {
    return (section.dataset.productHandles || '')
      .split('|')
      .filter(Boolean);
  }

  function getCount(section, cart) {
    const handles = getHandles(section);

    return (cart.items || []).reduce((sum, item) => {
      return sum + (
        handles.includes(item.handle) ? item.quantity : 0
      );
    }, 0);
  }

  function getSticky(section) {
    return [...document.querySelectorAll(
      '[data-brentos-bundle-sticky]'
    )].find(el => el.dataset.bundleId === section.id);
  }

  function render(section, cart) {
    const required = Number(section.dataset.required) || 4;
    const count = getCount(section, cart);
    const filled = Math.min(count, required);
    section.classList.toggle('is-complete', count >= required);

    const steps = section.querySelector('[data-bundle-steps]');
    const track = section.querySelector('[data-bundle-track]');
    const fill = section.querySelector('[data-bundle-fill]');
    const status = section.querySelector('[data-bundle-status]');

    if (steps) {
      steps.style.setProperty('--bundle-required', required);

      steps.replaceChildren(
        ...Array.from({ length: required }, (_, i) => {
          const el = document.createElement('div');
          el.className =
            `brentos-bundle__step${i < filled ? ' is-filled' : ''}`;
          el.textContent = i < filled ? '✓' : String(i + 1);
          return el;
        })
      );
    }

    if (track) {
      track.setAttribute('aria-valuenow', String(filled));
    }

    if (fill) {
      fill.style.width = `${filled / required * 100}%`;
    }

    if (status) {
      status.textContent = count >= required
        ? `🎉 Your bundle is ready! ${count} eligible items in your cart.`
        : `${count} of ${required} added — add ${required - count} more to unlock your offer!`;
    }

    renderSticky(section, count, required);
  }

function renderSticky(section, count, required) {
  const sticky = getSticky(section);
  if (!sticky) return;

  const filled = Math.min(count, required);
  const complete = count >= required;

  // Turn the progress pills green when the bundle is complete
  sticky.classList.toggle('is-complete', complete);

    const status = sticky.querySelector('[data-sticky-status]');
    const remaining = sticky.querySelector('[data-sticky-remaining]');
    const steps = sticky.querySelector('[data-sticky-steps]');
    const actions = sticky.querySelector('[data-sticky-actions]');

    if (status) {
      status.textContent = complete
        ? '🎉 Your bundle is ready!'
        : `${count} of ${required} ${section.dataset.productLabel || 'items'} added`;
    }

    if (remaining) {
      remaining.textContent = complete
        ? 'Offer unlocked!'
        : `${required - count} to go!`;
    }

    if (steps) {
      steps.style.setProperty('--bundle-required', required);

      steps.replaceChildren(
        ...Array.from({ length: required }, (_, i) => {
          const el = document.createElement('div');
          el.className =
            `brentos-bundle-sticky__step${i < filled ? ' is-filled' : ''}`;
          return el;
        })
      );
    }

    if (actions) {
      actions.hidden = !complete;
    }

    schedulePositionUpdate();
  }

  async function refresh() {
    const request = ++lastRequest;

    try {
      const response = await fetch(cartUrl, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Cart unavailable');

      const cart = await response.json();
      if (request !== lastRequest) return;

      // Reopen a dismissed footer only when the cart contents change.
      if (lastCart) {
        const oldItems = lastCart.items || [];
        const newItems = cart.items || [];

        const signature = items => JSON.stringify(
          items.map(item => [item.key, item.quantity])
            .sort((a, b) => String(a[0]).localeCompare(String(b[0])))
        );

        if (signature(oldItems) !== signature(newItems)) {
          dismissed.clear();
        }
      }

      lastCart = cart;

      getSections().forEach(section => render(section, cart));
      schedulePositionUpdate();

    } catch (error) {
      getSections().forEach(section => {
        const status = section.querySelector('[data-bundle-status]');

        if (status) {
          status.textContent =
            'Could not load bundle progress. Please refresh the page.';
        }
      });
    }
  }

  function scheduleRefresh(delay = 350) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, delay);
  }

  // Preserve the successful-add button confirmation.
  function showAddedConfirmation(productForm) {
    const productCard = productForm.closest('[data-js-product-item]');
    if (!productCard) return;

    const productLink = productCard.querySelector('a[href*="/products/"]');
    const productHandle = productLink?.href
      .split('/products/')[1]
      ?.split(/[?#/]/)[0];

    const eligible = getSections().some(section =>
      getHandles(section).includes(productHandle)
    );

    if (!eligible) return;

    const button = productForm.querySelector('[type="submit"]');
    if (!button) return;

    if (!button.dataset.brentosOriginalHtml) {
      button.dataset.brentosOriginalHtml = button.innerHTML;
    }

    clearTimeout(button.brentosConfirmationTimer);

    button.classList.add('brentos-bundle-added');
    button.textContent = '✓ Added to your bundle!';

    button.brentosConfirmationTimer = setTimeout(() => {
      button.innerHTML = button.dataset.brentosOriginalHtml;
      button.classList.remove('brentos-bundle-added');
      delete button.dataset.brentosOriginalHtml;
    }, 1500);
  }

  document.addEventListener('add-to-cart', event => {
    const productForm = event.target;

    if (
      productForm?.tagName === 'PRODUCT-FORM' &&
      productForm.closest('quick-add-to-cart')
    ) {
      showAddedConfirmation(productForm);
    }
  }, true);

  // Continue shopping dismisses the sticky footer.
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-bundle-continue]');
    if (!button) return;

    const sticky = button.closest('[data-brentos-bundle-sticky]');
    if (!sticky) return;

    dismissed.add(sticky.dataset.bundleId);
    schedulePositionUpdate();
  });

  // Avoid displaying the footer over Local's cart drawer.
  function cartDrawerVisible() {
    const drawer = document.getElementById('site-cart-sidebar');
    if (!drawer) return false;

    const style = window.getComputedStyle(drawer);
    const rect = drawer.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number(style.opacity) > 0 &&
      rect.width > 0 &&
      rect.height > 0 &&
      rect.right > 0 &&
      rect.left < window.innerWidth &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight
    );
  }

  function getHeaderHeight() {
    const selectors = [
      '#site-header',
      '[data-js-site-header]',
      '.site-header',
      'header[role="banner"]'
    ];

    for (const selector of selectors) {
      const header = document.querySelector(selector);
      if (!header) continue;

      const style = window.getComputedStyle(header);
      const rect = header.getBoundingClientRect();

      if (
        (style.position === 'sticky' || style.position === 'fixed') &&
        rect.bottom > 0 &&
        rect.top <= 0
      ) {
        return Math.max(0, rect.bottom);
      }
    }

    return 0;
  }

  function updateStickyPosition() {
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const drawerOpen = cartDrawerVisible();

    document.querySelectorAll('[data-brentos-bundle-sticky]')
      .forEach(sticky => {
        const section = document.getElementById(
          sticky.dataset.bundleId
        );

        const enabled = mobile
          ? sticky.dataset.position !== 'off'
          : sticky.dataset.desktopEnabled === 'true';

        if (!section || !enabled) {
          sticky.classList.remove('is-visible');
          sticky.setAttribute('aria-hidden', 'true');
          sticky.inert = true;
          return;
        }

        const topPosition =
          mobile && sticky.dataset.position === 'top';

        const headerHeight = topPosition ? getHeaderHeight() : 0;

        sticky.style.setProperty(
          '--brentos-sticky-header-height',
          `${headerHeight}px`
        );

        const pastOriginal =
          section.getBoundingClientRect().bottom <= headerHeight;

        const visible =
          pastOriginal &&
          !drawerOpen &&
          !dismissed.has(section.id);

        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', String(!visible));
        sticky.inert = !visible;
      });
  }

  function schedulePositionUpdate() {
    if (positionFrame !== null) return;

    positionFrame = requestAnimationFrame(() => {
      positionFrame = null;
      updateStickyPosition();
    });
  }

  // Watch Shopify AJAX cart requests.
  const originalFetch = window.fetch;

  window.fetch = function(...args) {
    const input = args[0];
    const url = typeof input === 'string'
      ? input
      : input?.url || '';

    const method = String(
      args[1]?.method || input?.method || 'GET'
    ).toUpperCase();

    const cartMutation =
      /\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/.test(url) &&
      method !== 'GET';

    const result = originalFetch.apply(this, args);

    if (cartMutation) {
      result.then(response => {
        if (response.ok) scheduleRefresh();
      }).catch(() => {});
    }

    return result;
  };

  document.addEventListener('submit', event => {
    if (event.target.closest('product-form')) {
      scheduleRefresh(1200);
    }
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refresh();
  });

  window.addEventListener('scroll', schedulePositionUpdate, {
    passive: true
  });

  window.addEventListener('resize', schedulePositionUpdate);

  window.addEventListener('pageshow', () => {
    refresh();
    schedulePositionUpdate();
  });

  document.addEventListener('shopify:section:load', () => {
    refresh();
    schedulePositionUpdate();
  });

  function observeCartDrawer() {
    const drawer = document.getElementById('site-cart-sidebar');
    if (!drawer) return;

    const observer = new MutationObserver(schedulePositionUpdate);

    observer.observe(drawer, {
      attributes: true,
      attributeFilter: [
        'class', 'style', 'aria-hidden', 'open'
      ]
    });
  }

  // Change Quick Buy labels only on Brentos bundle pages.
  

  // Change Quick Buy labels only on Brentos bundle pages.
  function updateBundleButtonLabels() {
    const sections = getSections();
    if (!sections.length) return;

    const eligibleHandles = new Set(
      sections.flatMap(section => getHandles(section))
    );

    document.querySelectorAll('[data-js-product-item]').forEach(card => {
      const productLink = card.querySelector('a[href*="/products/"]');
      const handle = productLink?.href
        .split('/products/')[1]
        ?.split(/[?#/]/)[0];

      if (!handle || !eligibleHandles.has(handle)) return;

      const button = card.querySelector('[data-js-product-add-to-cart]');
      const label = button?.querySelector('.button__text');

      if (!label || button.hasAttribute('disabled')) return;

      // T-shirts with variants open a Quick View popup.
      const hasOptionsPopup = Boolean(
        button.closest('quick-view-product')
      );

      label.textContent = hasOptionsPopup
        ? 'Choose size'
        : 'Add to bundle';
    });
  }

  updateBundleButtonLabels();

  document.addEventListener('shopify:section:load', () => {
    updateBundleButtonLabels();
  });



  observeCartDrawer();
  refresh();
  schedulePositionUpdate();
})();
